import { FolderOutlined } from '@mui/icons-material';
import { ListItemIcon, ListItemText } from '@mui/material';

import { useState } from 'react';

import { FolderSelector } from '@/modules/files/components/FolderSelector';

import { graphql, type DocumentType } from '@/gql';

import { useMutation } from '@/hooks/mutationWithSnackbar';

import type { OwnerPermisson } from '@/modules/files/hooks/useFilePermissions';

import {
  addNewFileToCache,
  removeFileFromCache,
} from '@/modules/files/utils/fileUpdateFunctions';

import type { ChangeOwnerDialogProps } from '../../../../actions/move/FileManagerChangeOwnerConfirmDialog';
import { FileManagerChangeOwnerConfirmDialog } from '../../../../actions/move/FileManagerChangeOwnerConfirmDialog';

import type { OwnerCandidate } from '../../FileOwnerSelector';

import { MaybeUsableMenuItem } from './MaybeUnusableMenuItem';

export const MOVE_FILE_MUTATION = graphql(`
  mutation MoveFile($data: MoveFileInput) {
    moveFile(params: $data) {
      file {
        ...fileFields
      }
    }
  }
`);

const MOVE_FILE_MENU_ITEM_FILE_FRAGMENT = graphql(`
  fragment moveFileMenuItemFileFields on File {
    ...fileFields
  }
`);

const MOVE_FILE_MENU_ITEM_FILES_FOR_OWNER_FRAGMENT = graphql(`
  fragment moveFileMenuItemFilesForOwnerFields on FilesForOwner {
    owner {
      ... on Group {
        _id
        name
      }
      ... on Service {
        _id
        name
      }
    }
  }
`);

interface Props {
  file: DocumentType<typeof MOVE_FILE_MENU_ITEM_FILE_FRAGMENT>;
  filesForOwner: DocumentType<
    typeof MOVE_FILE_MENU_ITEM_FILES_FOR_OWNER_FRAGMENT
  >;
  ownerCandidates: OwnerCandidate[];
  ownerPermission: OwnerPermisson | null;
  rootOwnerId: string;
  canEdit: boolean;
  canLockRoot: boolean;
  unusableExplanation: string | null;
  onClose: () => void;
}

export const MoveFileMenuItem = ({
  file,
  filesForOwner,
  ownerCandidates,
  ownerPermission,
  rootOwnerId,
  canEdit,
  canLockRoot,
  unusableExplanation,
  onClose,
}: Props) => {
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [changeOwnerDialogOpen, setChangeOwnerDialogOpen] =
    useState<ChangeOwnerDialogProps>();

  const [moveFile] = useMutation(MOVE_FILE_MUTATION);

  return (
    <>
      <MaybeUsableMenuItem
        isUsable={canEdit}
        isExpectedUsable={canLockRoot}
        unusableExplanation={unusableExplanation}
        onClick={() => {
          onClose();
          setSelectorOpen(true);
        }}
      >
        <ListItemIcon>
          <FolderOutlined />
        </ListItemIcon>
        <ListItemText primary="Move to..." />
      </MaybeUsableMenuItem>
      <FolderSelector
        title="Select a folder"
        fileActionName="Move"
        parentActionName="Move here"
        disableItemWithReason={(fileItem) => {
          if (file._id === fileItem._id) {
            return `Can't move "${fileItem.filename || ''}" into itself`;
          }

          if (!ownerPermission?.userCanEditFiles) {
            return `You don't have permission to move to "${file.filename || ''}"`;
          }
        }}
        startingParentId={file?.parent?._id}
        rootOwner={{
          _id: filesForOwner.owner?._id ?? '',
          type: filesForOwner.owner.__typename,
        }}
        open={selectorOpen}
        onClose={() => setSelectorOpen(false)}
        onSelect={(folderId, ownerId, folder) => {
          setSelectorOpen(false);

          const isOwnerIdChanging = ownerId !== file.owner?._id;

          // Opens dialog to get confirmation if the owner and visibility is changing
          if (isOwnerIdChanging) {
            const ownerName = ownerCandidates.find(
              (candidate) => candidate._id === ownerId,
            )?.name;

            if (!ownerName) {
              return;
            }

            setChangeOwnerDialogOpen({
              file,
              parentId: folder?._id,
              ownerId,
              rootOwnerId,
              isChangingParent: true,
              isFolder: true,
              isMovingToRoot: ownerId === rootOwnerId,
              ownerName,
              open: true,
              onClose: () => setChangeOwnerDialogOpen(undefined),
            });
            return;
          }

          moveFile({
            variables: {
              data: {
                file: file._id,
                parent: folderId ?? null,
                canMoveOwner: true,
                owner: null,
              },
            },
            optimisticResponse: isOwnerIdChanging
              ? undefined
              : {
                  moveFile: {
                    __typename: 'MoveFileResult',
                    file: {
                      ...file,
                      parent: folder ? folder : null,
                      owner: folder?.owner ?? null,
                    },
                  },
                },
            update(cache, { data }) {
              const newFile = data?.moveFile?.file;

              // Remove the file from the old parent
              removeFileFromCache(cache, file, rootOwnerId);
              // Add the file to the new parent
              addNewFileToCache(cache, newFile, rootOwnerId);
            },
          });
        }}
      />
      {changeOwnerDialogOpen ? (
        <FileManagerChangeOwnerConfirmDialog {...changeOwnerDialogOpen} />
      ) : null}
    </>
  );
};
